<div class="id-liveness-verification-report">
  <!-- Page Control for export of data -->
  <PageControl
    :title="'ID Liveness Verification Report'"
    :elements_query="sheet_elements"
  >
    <button
      @click="show_export_modal"
      class="btn btn-bg-green text-xs rounded-small shadow-sm me-2"
    >
      <i class="bi bi-download"></i>
      Export Data
    </button>

    <!-- Export Modal -->
    <Teleport to="body">
      <div
        class="modal fade"
        :id="'export-modal-' + report_reference"
        tabindex="-1"
        aria-labelledby="exportModalLabel"
        aria-hidden="true"
      >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content rounded-4 shadow">
          <div class="modal-header">
            <h6 class="modal-title fw-semibold text-muted">
              Export ID Liveness Verification Report Data
            </h6>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label fw-bold">Select Data to Export:</label>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  v-model="export_options.personal_information"
                  id="personalInformation"
                />
                <label class="form-check-label" for="personalInformation">
                  Personal Information (ID Document Details)
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  v-model="export_options.facial_comparison_liveness"
                  id="facialComparisonLiveness"
                />
                <label class="form-check-label" for="facialComparisonLiveness">
                  Facial Comparison & Liveness Analysis
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  v-model="export_options.verification_results"
                  id="verificationResults"
                />
                <label class="form-check-label" for="verificationResults">
                  Verification Results & Analysis
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  v-model="export_options.raw_json"
                  id="rawJson"
                />
                <label class="form-check-label" for="rawJson">
                  Raw JSON Response Data
                </label>
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label fw-bold">Export Format:</label>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  v-model="export_format"
                  value="csv"
                  id="csvFormat"
                />
                <label class="form-check-label" for="csvFormat">
                  CSV (Separate files for each table)
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  v-model="export_format"
                  value="excel"
                  id="excelFormat"
                />
                <label class="form-check-label" for="excelFormat">
                  Excel (Multiple sheets in one file)
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  v-model="export_format"
                  value="json"
                  id="jsonFormat"
                />
                <label class="form-check-label" for="jsonFormat">
                  JSON (JavaScript Object Notation)
                </label>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-bg-secondary text-xs rounded-small shadow-sm me-2"
              @click="close_modal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-bg-green text-xs rounded-small shadow-sm"
              @click="export_data"
              :disabled="!has_selected_tables"
            >
              <i class="bi bi-download me-1"></i>
              Export Data
            </button>
          </div>
        </div>
      </div>
    </div>
    </Teleport>
  </PageControl>

  <PageComponent>
    <SectionSheet>
      <div
        class="container-fluid py-3 header d-flex align-items-center justify-content-between border-bottom mb-3"
      >
        <div class="d-flex">
          <img
            class="logo"
            width="295"
            height="125"
            src="@/assets/images/new_black_logo2.svg"
            alt="Logo"
          />
        </div>
        <div class="d-flex text-end">
          <div>
            <div class="h4 fw-bold mb-1">ID Liveness Verification</div>
            <div class="text-muted text-xs">
              28 Fricker Rd,<br />
              Illovo, Johannesburg,<br />
              2196 South Africa<br />
              Website:
              <a
                href="https://www.goodpeopledata.com/"
                target="_blank"
                class="text-decoration-none text-muted"
                >www.goodpeopledata.com</a
              >
            </div>
          </div>
        </div>
      </div>

      <div class="card shadow-sm border bg-white mb-2">
        <div class="card-header">
          <h6 class="mb-0">ID Liveness Verification Summary</h6>
        </div>
        <div class="card-body text-xs">
          <p>
            This ID Liveness verification report presents the results for
            <span class="fw-bold text-dark">{{ full_name }}</span>, ID Number
            <span class="fw-bold text-dark">{{ id_number }}</span>,
            <span class="fw-bold text-dark">{{ marital_status }}</span>. The
            comprehensive verification process utilizes Active Liveness
            Verification to capture a live image of the individual, which is
            then matched against Home Affairs database records using the
            provided ID Number. The process consists of three primary steps:
            <br> 
            <br> 
            (1) Capturing a secure live selfie image through Active Liveness
            technology,
            <br> 
            (2) Querying the Home Affairs database using the ID
            Number, and 
            <br> 
            (3) Analyzing the facial match between the live image
            and the official Home Affairs portrait. The verification assessment
            results are summarized below:
          </p>
          <!-- Clean Status Cards for A4 Print -->
          <div class="row g-2">
            <div class="col-md-4">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-person-check"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">Liveness Status</div>
                  <div
                    class="status-value-simple bg-white"
                    :class="liveness_status_class"
                  >
                    {{ liveness_status_text }}
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-person-badge"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">Facial Match</div>
                  <div
                    class="status-value-simple bg-white"
                    :class="face_result_class"
                  >
                    {{ face_result_text }}
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-file-earmark-check"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">ID Verification</div>
                  <div
                    class="status-value-simple bg-white"
                    :class="id_verification_class"
                  >
                    {{ id_verification_text }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card shadow-sm border bg-white mb-2">
        <div class="card-header">
          <h6 class="mb-0">Personal Information</h6>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <!-- Full Name -->
            <div class="col-md-6">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-person-circle"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">Full Name</div>
                  <div class="status-value-simple bg-white">
                    {{ full_name }}
                  </div>
                </div>
              </div>
            </div>

            <!-- First Name -->
            <div class="col-md-6">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-person-badge"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">First Name</div>
                  <div class="status-value-simple bg-white">
                    {{ first_name }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Last Name -->
            <div class="col-md-6">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-person-vcard"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">Last Name</div>
                  <div class="status-value-simple bg-white">
                    {{ last_name }}
                  </div>
                </div>
              </div>
            </div>

            <!-- ID Number -->
            <div class="col-md-6">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-hash"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">ID Number</div>
                  <div class="status-value-simple bg-white">
                    {{ id_number }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Marital Status -->
            <div class="col-md-6">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-heart"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">Marital Status</div>
                  <div class="status-value-simple bg-white">
                    {{ marital_status }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Birth Place -->
            <div class="col-md-6">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-geo-alt"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">Birth Place</div>
                  <div class="status-value-simple bg-white">
                    {{ birth_place }}
                  </div>
                </div>
              </div>
            </div>

            <!-- ID Issue Date -->
            <div class="col-md-6">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-calendar-event"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">ID Issue Date</div>
                  <div class="status-value-simple bg-white">
                    {{ formatted_id_issue_date }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Smart Card Status -->
            <div class="col-md-6">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-credit-card"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">Smart Card</div>
                  <div class="status-value-simple bg-white">
                    {{ smart_card_status }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="report-meta">
        <div><span class="text-muted">Ref:</span> {{ report_reference }}</div>
        <div>
          <span class="text-muted">Generated:</span> {{ generated_date }}
        </div>
      </div>
    </SectionSheet>

    <SectionSheet>
      <!-- Facial Comparison & Liveness Analysis -->
      <div class="card shadow-sm border bg-white mb-2">
        <div class="card-header">
          <h6 class="mb-0">Facial Comparison & Liveness Analysis</h6>
        </div>
        <div class="card-body">
          
          <!-- Images Section -->
          <div class="row g-3">
            <div class="col-md-6">
              <div class="image-section">
                <div class="image-title">Facial Image (Official Record)</div>
                <div class="image-container">
                  <img
                    v-if="facial_image_src"
                    :src="facial_image_src"
                    alt="Facial Image"
                    class="verification-image"
                  />
                  <div v-else class="no-image">
                    <i class="bi bi-image"></i>
                    <p>No facial image available</p>
                  </div>
                </div>
                <div class="image-quality-info">
                  <div class="quality-item">
                    <span class="quality-label">Source</span>
                    <span class="quality-value">Official Record</span>
                  </div>
                  <div class="quality-item">
                    <span class="quality-label">Available</span>
                    <span class="quality-value"
                      >{{ facial_image_available ? 'Yes' : 'No' }}</span
                    >
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="image-section">
                <div class="image-title">Comparison Image (Selfie)</div>
                <div class="image-container">
                  <img
                  v-if="customer_image_src"
                  :src="customer_image_src"
                  alt="Comparison Image"
                  class="verification-image"
                  />
                  <div v-else class="no-image">
                  <i class="bi bi-camera"></i>
                  <p>Comparison image not displayed</p>
                  </div>
                </div>
                <div class="image-quality-info">
                  <div class="quality-item">
                    <span class="quality-label">Type</span>
                    <span class="quality-value">Live Selfie</span>
                  </div>
                  <div class="quality-item">
                    <span class="quality-label">Status</span>
                    <span class="quality-value">Analyzed</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Enhanced Analysis Cards -->
          <div class="row g-3 mb-4">
            <div class="col-md-6">
              <div class="analysis-card">
                <div class="analysis-icon liveness">
                  <i class="bi bi-eye"></i>
                </div>
                <div class="analysis-content">
                  <div class="analysis-label">Liveness Detection</div>
                  <div class="analysis-value">{{ liveness_result_text }}</div>
                  <div class="analysis-detail">{{ liveness_message }}</div>
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="analysis-card">
                <div class="analysis-icon confidence">
                  <i class="bi bi-percent"></i>
                </div>
                <div class="analysis-content">
                  <div class="analysis-label">Face Match Confidence</div>
                  <div class="analysis-value">
                    {{ face_confidence_percentage }}%
                  </div>
                  <div class="analysis-detail">
                    Confidence: {{ face_confidence }}
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="analysis-card">
                <div class="analysis-icon quality">
                  <i class="bi bi-image"></i>
                </div>
                <div class="analysis-content">
                  <div class="analysis-label">Liveness Quality</div>
                  <div class="analysis-value">
                    {{ liveness_quality_percentage }}%
                  </div>
                  <div class="analysis-detail">
                    Score: {{ liveness_quality_score }}
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="analysis-card">
                <div class="analysis-icon assessment">
                  <i class="bi bi-award"></i>
                </div>
                <div class="analysis-content">
                  <div class="analysis-label">Liveness Probability</div>
                  <div class="analysis-value">
                    {{ liveness_probability_percentage }}%
                  </div>
                  <div class="analysis-detail">
                    Raw Score: {{ liveness_result_score }}
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <div class="report-meta">
        <div><span class="text-muted">Ref:</span> {{ report_reference }}</div>
        <div>
          <span class="text-muted">Generated:</span> {{ generated_date }}
        </div>
      </div>
    </SectionSheet>

    <SectionSheet>
      <div class="card shadow-sm border bg-white mb-3">
        <div
          class="card-header bg-light d-flex justify-content-between align-items-center"
        >
          <h6 class="mb-0">ID Verification Analysis</h6>
        </div>
        <div class="card-body">
          <!-- Verification Results -->
          <h6 class="mb-3 border-bottom pb-2">
            <i class="bi bi-check-square me-2"></i>Verification Results
          </h6>
          <div class="table-responsive mb-4">
            <table class="table table-bordered align-middle">
              <thead class="table-light">
                <tr>
                  <th>Verification Check</th>
                  <th>Result</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <i class="bi bi-person-check me-2 text-secondary"></i>First
                    Name Match
                  </td>
                  <td>
                    <span class="badge" :class="first_name_result_class">
                      {{ first_name_result }}%
                    </span>
                  </td>
                  <td>
                    Percentage match between provided first name and official
                    record.
                  </td>
                </tr>
                <tr>
                  <td>
                    <i class="bi bi-person-vcard me-2 text-secondary"></i>Last
                    Name Match
                  </td>
                  <td>
                    <span class="badge" :class="last_name_result_class">
                      {{ last_name_result }}%
                    </span>
                  </td>
                  <td>
                    Percentage match between provided last name and official
                    record.
                  </td>
                </tr>
                <tr>
                  <td>
                    <i class="bi bi-person-badge me-2 text-secondary"></i>Face
                    Verification
                  </td>
                  <td>
                    <span class="badge" :class="face_result_class">
                      {{ face_result_status }}
                    </span>
                  </td>
                  <td>
                    Facial comparison between live image and official portrait.
                  </td>
                </tr>
                <tr>
                  <td>
                    <i class="bi bi-eye me-2 text-secondary"></i>Liveness Check
                  </td>
                  <td>
                    <span class="badge" :class="liveness_pass_class">
                      {{ liveness_pass_text }}
                    </span>
                  </td>
                  <td>
                    Verification that the submitted image is from a live person.
                  </td>
                </tr>
                <tr>
                  <td>
                    <i class="bi bi-shield-check me-2 text-secondary"></i>ID
                    Verification
                  </td>
                  <td>
                    <span class="badge" :class="overall_status_class">
                      {{ overall_status_text }}
                    </span>
                  </td>
                  <td>Overall verification status combining all checks.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Additional Information -->
          <h6 class="mb-3 border-bottom pb-2">
            <i class="bi bi-info-circle me-2"></i>Additional Information
          </h6>
          <div class="table-responsive">
            <table class="table table-bordered align-middle">
              <thead class="table-light">
                <tr>
                  <th>Property</th>
                  <th>Value</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <i class="bi bi-fingerprint me-2 text-secondary"></i
                    >HANIS Tracking Number
                  </td>
                  <td>{{ tracking_number }}</td>
                  <td>Unique identifier for this verification request.</td>
                </tr>
                <tr>
                  <td>
                    <i class="bi bi-clock me-2 text-secondary"></i>Dead
                    Indicator
                  </td>
                  <td>{{ dead_indicator_text }}</td>
                  <td>Indicates if the person is recorded as deceased.</td>
                </tr>
                <tr>
                  <td>
                    <i class="bi bi-shield-x me-2 text-secondary"></i>ID Blocked
                  </td>
                  <td>{{ id_blocked_text }}</td>
                  <td>Indicates if the ID is blocked or restricted.</td>
                </tr>
                <tr>
                  <td>
                    <i class="bi bi-file-text me-2 text-secondary"></i>On HANIS
                  </td>
                  <td>{{ on_hanis_text }}</td>
                  <td>
                    Present on Home Affairs National Identification System.
                  </td>
                </tr>
                <tr>
                  <td>
                    <i class="bi bi-people me-2 text-secondary"></i>On NPR
                  </td>
                  <td>{{ on_npr_text }}</td>
                  <td>Present on National Population Register.</td>
                </tr>
                <tr>
                  <td>
                    <i class="bi bi-wallet me-2 text-secondary"></i>Wallet
                    Created
                  </td>
                  <td>{{ wallet_created_text }}</td>
                  <td>Indicates if a digital wallet has been created.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="report-meta">
        <div><span class="text-muted">Ref:</span> {{ report_reference }}</div>
        <div>
          <span class="text-muted">Generated:</span> {{ generated_date }}
        </div>
      </div>
    </SectionSheet>

    <!-- Disclaimer and Information Section -->
    <SectionSheet>
      <div class="card shadow-sm border bg-white mb-2">
        <div class="card-header bg-light">
          <h6 class="mb-0">Disclaimer and Information</h6>
        </div>
        <div class="card-body">
          <div class="disclaimer-content">
            <h6 class="mb-3 border-bottom pb-2">
              <i class="bi bi-info-circle me-2"></i>General Information
            </h6>
            <p class="text-xs mb-4">
              This ID Liveness Verification Report provides a technical
              assessment of an individual's identity using Active Liveness
              Verification technology and Home Affairs database matching. The
              verification process captures a secure live selfie of the
              individual, queries the Home Affairs database using the provided
              ID Number, and performs advanced facial comparison analysis to
              prevent identity fraud and impersonation.
            </p>

            <h6 class="mb-3 border-bottom pb-2">
              <i class="bi bi-shield-lock me-2"></i>Data Privacy
            </h6>
            <p class="text-xs mb-4">
              All biometric data and personal information are processed in
              accordance with applicable data protection laws including POPIA
              (Protection of Personal Information Act). This report should only
              be shared with authorized personnel who require access to this
              information for legitimate business purposes.
            </p>

            <h6 class="mb-3 border-bottom pb-2">
              <i class="bi bi-gear me-2"></i>Technical Notes
            </h6>
            <p class="text-xs mb-4">
              Verification scores and confidence levels are calculated using
              proprietary algorithms that measure the likelihood of document
              authenticity and facial matching. Results are based on official
              South African government databases including HANIS and NPR
              systems. Environmental factors such as lighting, image quality,
              and camera specifications may impact verification results.
            </p>

            <div class="text-center text-muted mt-3">
              <p class="mb-0 text-xs">
                © {{ new Date().getFullYear() }} The Good People Data Company.
                All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="report-meta">
        <div><span class="text-muted">Ref:</span> {{ report_reference }}</div>
        <div>
          <span class="text-muted">Generated:</span> {{ generated_date }}
        </div>
      </div>
    </SectionSheet>
  </PageComponent>
</div>
